// UI
import {
  Anchor,
  Box,
  Button,
  CheckBox,
  FormField,
  Heading,
  MaskedInput,
  Paragraph,
  Select,
  Text,
  TextInput,
} from "grommet";
import PropTypes from "prop-types";
import React, { useRef } from "react";
import styled from "styled-components";
import { callCep } from "./ApiService";
import Welcome from "./Welcome";

const Information = styled.p`
  font-size: 12px;
  color: salmon;
`;

const getParam = (param) => window.location.href.split(param).pop();

const PHONE_REGEX = [
  { fixed: "(" },
  {
    length: 2,
    regexp: /^[0-9]{1,2}$/,
    placeholder: "xx",
  },
  { fixed: ")" },
  {
    length: 5,
    regexp: /^[0-9]{1,5}$/,
    placeholder: "xxxxx",
  },
  {
    length: 4,
    regexp: /^[0-9]{1,4}$/,
    placeholder: "xxxx",
  },
];

const DATE_REGEX = [
  {
    length: 2,
    regexp: /^[0-9]{1,2}$/,
    placeholder: "dd",
  },
  { fixed: "/" },
  {
    length: 2,
    regexp: /^[0-9]{1,2}$/,
    placeholder: "mm",
  },
  { fixed: "/" },
  {
    length: 4,
    regexp: /^[0-9]{1,4}$/,
    placeholder: "aaaa",
  },
];

const persons = ["Pessoa física", "Pessoa jurídica"];

const taxRegimes = [
  "Simples Nacional",
  "Lucro Real",
  "Lucro Presumido",
  "Lucro Arbitrado",
];

const translatePersonOption = (person) =>
  person === "Pessoa física" ? "person" : "company";
const convertPersonOption = (person) =>
  person === "person" ? persons[0] : persons[1];

export const CPF_MASK_INPUT_REGEX = [
  {
    length: 3,
    regexp: /^[0-9]{1,3}$/,
    placeholder: "   ",
  },
  { fixed: "." },
  {
    length: 3,
    regexp: /^[0-9]{1,3}$/,
    placeholder: "   ",
  },
  { fixed: "." },
  {
    length: 3,
    regexp: /^[0-9]{1,3}$/,
    placeholder: "   ",
  },
  { fixed: "-" },
  {
    length: 2,
    regexp: /^[0-9]{1,2}$/,
    placeholder: "   ",
  },
];

export const ZIPCODE_REGEX = [
  {
    length: 5,
    regexp: /^[0-9]{1,5}$/,
    placeholder: "   ",
  },
  { fixed: "-" },
  {
    length: 3,
    regexp: /^[0-9]{1,3}$/,
    placeholder: "   ",
  },
];

const handleCepChange = (e, setFieldValue, addressNumberInput) => {
  const value = e.target.value.replace("-", "");

  setFieldValue("address_zipcode", e.target.value);

  if (value.length !== 8) {
    return;
  }

  callCep(value).then(({ data }) => {
    if (data.erro) return;

    setFieldValue("address", data.logradouro);
    setFieldValue("address_complement", data.complemento);
    setFieldValue("address_neighborhood", data.bairro);
    setFieldValue("address_state_code", data.uf);
    setFieldValue("address_city_name", data.localidade);
    addressNumberInput.current.focus();
  });
};

const badEmails = ["hotmail", "outlook"];

const TermsLabel = () => (
  <Text size="small" color="dark-1" margin={{ bottom: "small" }}>
    Ao prosseguir, estou ciente de que li e concordo com os{" "}
    <Anchor href="/termos-de-uso" target="_blank">
      Termos de Uso
    </Anchor>{" "}
    e o{" "}
    <Anchor href="/termo-de-consentimento" target="_blank">
      Termo de Consentimento
    </Anchor>{" "}
    .
  </Text>
);

export default function SignUp({
  values,
  touched,
  errors,
  isSubmitting,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
}) {
  const addressNumberInput = useRef();

  if (getParam("?account_created=") === "true") {
    return (
      <Box pad="small">
        <Box
          background="status-ok"
          pad={{ horizontal: "medium", vertical: "small" }}
          round="small"
        >
          <Text color="white">
            Sua conta foi criada! Agora precisamos confirmar sua identidade, ok?
          </Text>
        </Box>

        <Paragraph margin={{ vertical: "small" }} size="small">
          Verifique seu e-mail e clique no endereço enviado para confirmar sua
          conta.
        </Paragraph>

        <Paragraph>Dúvidas?</Paragraph>

        <Button
          primary
          onClick={() =>
            window.open(
              "https://wa.me/558130896015?text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20na%20TRADERTAX!"
            )
          }
          label="Abrir Atendimento"
        />
      </Box>
    );
  }

  if (getParam("?account_confirmed=") === "true") {
    return (
      <Box pad="small">
        <Box
          background="status-ok"
          pad={{ horizontal: "medium", vertical: "small" }}
          round="small"
        >
          <Text color="white">Sua conta foi confirmada com sucesso!</Text>
        </Box>

        <Welcome />
      </Box>
    );
  }

  const isBadEmail = badEmails.reduce((acc, item) => {
    if (!values.email) return false;
    if (acc) return acc;

    return values.email.indexOf(item) > -1;
  }, false);

  return (
    <Box fill>
      <Text size="small">
        Preencha os dados abaixo para criar sua conta. Após confirmação, você
        receberá um e-mail de boas vindas com maiores explicações.
      </Text>

      <Heading level={4} margin={{ bottom: "none" }}>
        Dados Gerais
      </Heading>
      <Box pad={{ vertical: "small" }}>
        <form onSubmit={handleSubmit}>
          <FormField
            label="Você está se cadastrando como:"
            error={
              errors.person_type && touched.person_type
                ? errors.person_type
                : null
            }
            value={values.person_type}
          >
            <Select
              id="person_type"
              name="person_type"
              value={convertPersonOption(values.person_type)}
              options={persons}
              onChange={({ option }) => {
                setFieldValue("person_type", translatePersonOption(option));
              }}
            />
          </FormField>

          <FormField
            label={
              values.person_type === "person" ? "Nome completo" : "Razão Social"
            }
            error={errors.name && touched.name ? errors.name : null}
            name="name"
            htmlFor="name"
          >
            <TextInput
              id="name"
              autoFocus
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
              plain
            />
          </FormField>

          <Box direction="row" alignContent="around">
            <Box margin={{ right: "small" }} fill>
              <FormField
                label={values.person_type === "person" ? "CPF" : "CNPJ"}
                error={errors.cpf}
                htmlFor="cpf"
                name="cpf"
              >
                <TextInput
                  id="cpf"
                  value={values.cpf}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  plain
                  placeholder=""
                />
              </FormField>
            </Box>

            <Box fill>
              <FormField
                label="Telefone / Whatsapp"
                error={errors.phone}
                name="phone"
                htmlFor="phone"
              >
                <MaskedInput
                  id="phone"
                  name="phone"
                  onBlur={handleBlur}
                  onChange={(e) => setFieldValue("phone", e.target.value)}
                  value={values.phone}
                  mask={PHONE_REGEX}
                />
              </FormField>
            </Box>
          </Box>

          {values.person_type === "company" && (
            <FormField
              label="Regime tributário da empresa"
              error={
                errors.company_tax_regime && touched.company_tax_regime
                  ? errors.company_tax_regime
                  : null
              }
              value={values.company_tax_regime}
            >
              <Select
                id="company_tax_regime"
                name="company_tax_regime"
                value={values.company_tax_regime}
                options={taxRegimes}
                onChange={({ option }) => {
                  setFieldValue("company_tax_regime", option);
                }}
              />
            </FormField>
          )}

          {values.person_type === "company" && (
            <FormField
              label="Nome completo do Responsável pela conta"
              error={
                errors.company_responsible_name &&
                touched.company_responsible_name
                  ? errors.company_responsible_name
                  : null
              }
              name="company_responsible_name"
              htmlFor="company_responsible_name"
            >
              <TextInput
                id="company_responsible_name"
                value={values.company_responsible_name}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting}
                plain
              />
            </FormField>
          )}

          <Box direction="row" alignContent="around">
            <Box margin={{ right: "small" }} fill>
              <FormField
                label="Seu e-mail"
                error={errors.email && touched.email ? errors.email : null}
                name="email"
                htmlFor="email"
              >
                <TextInput
                  id="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  plain
                />
              </FormField>

              {isBadEmail && (
                <Information>
                  Se você utiliza Hotmail ou Outlook, recomendamos fortemente
                  que utilize outro provedor, pois tudo será enviado para seu
                  e-mail e estes provedores causam constantes problemas nos
                  envios.
                </Information>
              )}
            </Box>

            <Box fill>
              <FormField
                label="Data de Nascimento"
                error={errors.date_of_birth}
                name="date_of_birth"
                htmlFor="date_of_birth"
              >
                <MaskedInput
                  id="date_of_birth"
                  name="date_of_birth"
                  onBlur={handleBlur}
                  onChange={(e) =>
                    setFieldValue("date_of_birth", e.target.value)
                  }
                  value={values.date_of_birth}
                  mask={DATE_REGEX}
                />
              </FormField>
            </Box>
          </Box>

          <FormField
            error={errors.password && touched.password ? errors.password : null}
            label="Cria uma senha (Mínino de 8 dígitos)"
            name="password"
            htmlFor="password"
          >
            <TextInput
              id="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
              plain
              type="password"
            />
          </FormField>

          {/* ENDERECO */}
          <Heading level={4}>Preencha seu endereço</Heading>

          <FormField
            label="CEP"
            error={
              errors.address_zipcode && touched.address_zipcode
                ? errors.address_zipcode
                : null
            }
            name="address_zipcode"
            htmlFor="address_zipcode"
          >
            <MaskedInput
              id="address_zipcode"
              name="address_zipcode"
              onBlur={handleBlur}
              onChange={(e) =>
                handleCepChange(e, setFieldValue, addressNumberInput)
              }
              value={values.address_zipcode}
              mask={ZIPCODE_REGEX}
            />
          </FormField>

          <Box direction="row" alignContent="around">
            <Box margin={{ right: "small" }} fill>
              <FormField
                error={
                  errors.address && touched.address ? errors.address : null
                }
                label="Endereço"
                name="address"
                htmlFor="address"
              >
                <TextInput
                  id="address"
                  value={values.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  plain
                  type="text"
                />
              </FormField>
            </Box>

            <Box>
              <FormField
                error={
                  errors.address_number && touched.address_number
                    ? errors.address_number
                    : null
                }
                label="Número"
                name="address_number"
                htmlFor="address_number"
              >
                <TextInput
                  id="address_number"
                  value={values.address_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  plain
                  type="text"
                  ref={addressNumberInput}
                />
              </FormField>
            </Box>
          </Box>

          <Box direction="row" alignContent="around">
            <Box margin={{ right: "small" }} fill>
              <FormField
                error={
                  errors.address_neighborhood && touched.address_neighborhood
                    ? errors.address_neighborhood
                    : null
                }
                label="Bairro"
                name="address_neighborhood"
                htmlFor="address_neighborhood"
              >
                <TextInput
                  id="address_neighborhood"
                  value={values.address_neighborhood}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  plain
                  type="text"
                />
              </FormField>
            </Box>

            <Box fill>
              <FormField
                error={
                  errors.address_city_name && touched.address_city_name
                    ? errors.address_city_name
                    : null
                }
                label="Cidade"
                name="address_city_name"
                htmlFor="address_city_name"
              >
                <TextInput
                  id="address_city_name"
                  value={values.address_city_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  plain
                  type="text"
                />
              </FormField>
            </Box>
          </Box>

          <Box direction="row" alignContent="around">
            <Box margin={{ right: "small" }}>
              <FormField
                error={
                  errors.address_state_code && touched.address_state_code
                    ? errors.address_state_code
                    : null
                }
                label="Estado"
                name="address_state_code"
                htmlFor="address_state_code"
              >
                <TextInput
                  id="address_state_code"
                  value={values.address_state_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  plain
                  type="text"
                />
              </FormField>
            </Box>

            <Box fill>
              <FormField
                error={
                  errors.address_complement && touched.address_complement
                    ? errors.address_complement
                    : null
                }
                label="Complemento"
                name="address_complement"
                htmlFor="address_complement"
              >
                <TextInput
                  id="address_complement"
                  value={values.address_complement}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSubmitting}
                  plain
                  type="text"
                />
              </FormField>
            </Box>
          </Box>

          {/* ENDERECO */}

          <Heading level={4} margin={{ bottom: "none" }}>
            Seu Perfil
          </Heading>
          <p>Você já operou com renda variável em meses passados?</p>

          <FormField
            label={false}
            error={
              errors.have_history_apuration && touched.have_history_apuration
                ? errors.have_history_apuration
                : null
            }
            value={values.have_history_apuration}
          >
            <Select
              id="have_history_apuration"
              name="have_history_apuration"
              value={values.have_history_apuration}
              options={[
                "Não, este é meu primeiro mês",
                "Sim e possuo impostos atrasados",
                "Sim mas meus impostos estão em dia",
              ]}
              onChange={({ option }) =>
                setFieldValue("have_history_apuration", option)
              }
            />
          </FormField>
          <p>Como nos conheceu? Indique-nos ou coloque o código do parceiro:</p>

          <FormField
            label={false}
            error={
              errors.broker_manager && touched.broker_manager
                ? errors.broker_manager
                : null
            }
            name="broker_manager"
            htmlFor="broker_manager"
          >
            <TextInput
              id="broker_manager"
              value={values.broker_manager}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSubmitting}
              plain
              placeholder="Assessor, Empresa, Grupo ou Google"
            />
          </FormField>

          <br />

          <p>
            O seu cadastro passará por uma avaliação de risco por nossa equipe,
            podendo ser necessário o envio de maiores informações via e-mail.
            Entraremos em contato através do telefone informado ou via e-mail,
            caso seja necessário.
          </p>

          <p>
            Caso você tenha informado um código de assessor, você poderá receber
            um e-mail com um pedido de autorização de acesso as suas
            informações. Será necessário fazer login para aceitar o termo.
          </p>

          <FormField
            label={false}
            error={
              errors.terms_of_use && touched.terms_of_use
                ? errors.terms_of_use
                : null
            }
            name="terms_of_use"
            htmlFor="terms_of_use"
          >
            <CheckBox
              id="terms_of_use"
              value={values.terms_of_use}
              onBlur={handleBlur}
              name="terms_of_use"
              checked={!!values.terms_of_use}
              label={<TermsLabel />}
              onChange={(e) => {
                e.preventDefault();
                setFieldValue("terms_of_use", !!e.target.checked);
              }}
            />
          </FormField>

          <Box margin={{ top: "medium" }}>
            <Button
              type="submit"
              disabled={isSubmitting || !values.terms_of_use}
              label={isSubmitting ? "Um momento..." : "Criar minha conta"}
              primary
            />
          </Box>
        </form>
      </Box>
    </Box>
  );
}

SignUp.propTypes = {
  values: PropTypes.shape({}).isRequired,
  touched: PropTypes.shape({}).isRequired,
  status: PropTypes.shape({}),
  errors: PropTypes.shape({}).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

SignUp.defaultProps = {
  status: undefined,
};
